// transactionsDataGrid.config.ts
import type { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'

export const EC_COLUMN_BLOCK = '__EC__' as const

export type ColConfig = {
  order: string[]
  exclude: string[]
  pinnedLeft: string[]
  pinnedRight: string[]
}

/**
 * Only define the contexts you want to override.
 * Any other TransactionReportType will fall back to `default`.
 */
const configByContext = (managerReviewEnabled: boolean) => ({
  default: {
    order: [
      'date',
      'merchant',
      'originalMerchant',
      'card',
      'cardUser',
      'team',
      'cardholderEmail',
      'amount',
      'currency',
      'authBillingAmountCents',
      'authBillingCurrency',
      'authMerchantAmountCents',
      'authMerchantCurrency',
      'authExchangeRate',
      'attachmentsCount',
      'creditCardDisplayName',
      'creditCardType',
      'receipts',
      'txnSynced',
      'receiptsSynced',
      EC_COLUMN_BLOCK,
    ],
    exclude: ['managerReviewStatus', 'finalReview', 'status'],
    pinnedLeft: ['merchant'],
    pinnedRight: ['amount'],
  },
  [TransactionReportType.AccountingTransactions]: {
    order: [
      'date',
      'merchant',
      'originalMerchant',
      'amount',
      'card',
      'cardUser',
      'team',
      'cardholderEmail',
      ...(managerReviewEnabled ? ['managerReviewStatus'] : []),
      'finalReview',
      'currency',
      'authBillingAmountCents',
      'authBillingCurrency',
      'authMerchantAmountCents',
      'authMerchantCurrency',
      'authExchangeRate',
      'attachmentsCount',
      'creditCardDisplayName',
      'creditCardType',
      'receipts',
      'txnSynced',
      'receiptsSynced',
      EC_COLUMN_BLOCK,
    ],
    exclude: [
      ...(managerReviewEnabled ? [] : ['managerReviewStatus']),
      'status'
    ],
    pinnedLeft: [],
    pinnedRight: [
      ...(managerReviewEnabled ? ['managerReviewStatus'] : []),
      'finalReview',
    ],
  },
  [TransactionReportType.NeedsApproval]: {
    order: [
      'date',
      'merchant',
      'originalMerchant',
      'amount',
      'card',
      'cardUser',
      'team',
      'cardholderEmail',
      'currency',
      'authBillingAmountCents',
      'authBillingCurrency',
      'authMerchantAmountCents',
      'authMerchantCurrency',
      'authExchangeRate',
      'attachmentsCount',
      'creditCardDisplayName',
      'creditCardType',
      'receipts',
      'txnSynced',
      'receiptsSynced',
      'status',
      EC_COLUMN_BLOCK,
    ],
    exclude: ['managerReviewStatus', 'finalReview'],
    pinnedLeft: ['merchant'],
    pinnedRight: ['approval-actions'],
  },
  [TransactionReportType.ExpenseReport]: {
    order: [
      'date',
      'merchant',
      'originalMerchant',
      'amount',
      'card',
      'cardUser',
      EC_COLUMN_BLOCK,
      'receipts',
    ],
    exclude: [
      'status',
      'team',
      'txnSynced',
      'receiptsSynced',
    ],
    pinnedLeft: ['merchant'],
    pinnedRight: ['amount'],
  },
} as const satisfies {
  default: ColConfig
  // Only some enum members are present; that’s fine.
  // Anything missing will use `default`.
} & Partial<Record<TransactionReportType, ColConfig>>)

const pickConfig = (context?: TransactionReportType, managerReviewEnabled?: boolean): ColConfig => {
  const config = configByContext(managerReviewEnabled ?? false)

  if (context && context in config) {
    return (config as Partial<Record<TransactionReportType, ColConfig>>)[context]!
  }

  return config.default
}

/**
 * Public API: give me your raw columns and I’ll return:
 * - ordered columns according to context config
 * - pinned config to drop into DataGrid.initialState
 */
export function getConfiguredColumns<T extends GridValidRowModel>(
  context: TransactionReportType | undefined,
  allColumns: Array<GridColDef<T>>,
  managerReviewEnabled: boolean
): {
  columns: Array<GridColDef<T>>
  pinnedLeft: string[]
  pinnedRight: string[]
} {
  const cfg = pickConfig(context, managerReviewEnabled)

  // 1) exclude
  const available = allColumns.filter(
    (c) => !cfg.exclude.includes(String(c.field)),
  )

  // 2) bucket EC columns & map others
  const ecCols: Array<GridColDef<T>> = []
  const map = new Map<string, GridColDef<T>>()
  for (const col of available) {
    const key = String(col.field)
    if (key.startsWith('ec:')) ecCols.push(col)
    else map.set(key, col)
  }

  // 3) build ordered list per cfg.order, splicing ECs where the block is
  const ordered: Array<GridColDef<T>> = []
  for (const key of cfg.order) {
    if (key === EC_COLUMN_BLOCK) {
      for (const ec of ecCols) ordered.push(ec)
      ecCols.length = 0
      continue
    }
    const col = map.get(key)
    if (col) {
      ordered.push(col)
      map.delete(key)
    }
  }

  // 4) append leftovers (actions/chevron/etc.)
  for (const col of map.values()) ordered.push(col)
  // 5) safety: if config forgot EC block, append ECs at the end
  for (const ec of ecCols) ordered.push(ec)

  return {
    columns: ordered,
    pinnedLeft: cfg.pinnedLeft,
    pinnedRight: cfg.pinnedRight,
  }
}
